import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../shared/api/client";
import type {
  AllocationStatus,
  AllocationView,
  PageResponse,
  ProjectRole,
  ProjectSummary,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select } from "../../shared/ui";
import { PROJECT_ROLE_LABELS } from "../projects/projectVocabulary";
import { ALLOCATION_STATUS_LABELS } from "./resourceVocabulary";

const ROLES: ProjectRole[] = [
  "PROJECT_MANAGER", "TEAM_LEAD", "DEVELOPER", "QA", "DESIGNER", "DEVOPS", "BA", "STAKEHOLDER",
];

const STATUSES: AllocationStatus[] = ["PLANNED", "CONFIRMED", "COMPLETED", "CANCELLED"];

type Mode = "adjust" | "close" | "reassign";

/**
 * Changing an allocation that already exists (§36, §46).
 *
 * <p>Three separate acts rather than one editable form, because they mean different things to the
 * plan. Adjusting says the arrangement was always meant to be this; closing says it ran and has
 * now ended; reassigning says they moved to something else on a date. Collapsing them would make
 * "Ravi was 50% on Payments until March" indistinguishable from "Ravi was never on Payments".
 *
 * <p>There is no delete, deliberately. The period happened, the hours were planned against it,
 * and removing the row would silently change what past weeks looked like.
 */
export function AllocationDrawer({
  allocation,
  mode,
  open,
  onClose,
  onSaved,
}: {
  allocation: AllocationView | null;
  mode: Mode;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const [percentage, setPercentage] = useState("50");
  const [projectRole, setProjectRole] = useState<ProjectRole>("DEVELOPER");
  const [status, setStatus] = useState<AllocationStatus>("CONFIRMED");
  const [endDate, setEndDate] = useState("");
  const [toProjectId, setToProjectId] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const projects = useQuery({
    queryKey: ["projects", "picker"],
    queryFn: () =>
      api.get<PageResponse<ProjectSummary>>("/api/v1/projects?size=200&sort=name,asc"),
    enabled: open && mode === "reassign",
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!open || !allocation) {
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    setPercentage(String(Math.round(Number(allocation.allocationPercentage))));
    setProjectRole(allocation.projectRole);
    setStatus(allocation.allocationStatus);
    setEndDate(allocation.allocationEnd ?? today);
    setEffectiveFrom(today);
    setToProjectId("");
    setError(null);
  }, [open, allocation, mode]);

  const share = Number(percentage);
  const shareValid = !Number.isNaN(share) && share > 0 && share <= 100;

  const canSubmit =
    allocation != null
    && (mode === "adjust"
      ? shareValid
      : mode === "close"
        ? endDate !== ""
        : toProjectId !== "" && effectiveFrom !== "" && shareValid);

  const submit = async () => {
    if (!allocation) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (mode === "adjust") {
        await api.patch(`/api/v1/resources/allocations/${allocation.id}`, {
          allocationPercentage: share,
          projectRole,
          teamId: allocation.teamId,
          allocationStatus: status,
          version: allocation.version,
        });
      } else if (mode === "close") {
        await api.post(`/api/v1/resources/allocations/${allocation.id}/close`, {
          allocationEnd: endDate,
        });
      } else {
        await api.post("/api/v1/resources/allocations/reassign", {
          allocationId: allocation.id,
          effectiveFrom,
          toProjectId,
          projectRole,
          allocationPercentage: share,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["person-allocations"] });
      await queryClient.invalidateQueries({ queryKey: ["person-capacity"] });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save that.");
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === "adjust"
      ? "Change this allocation"
      : mode === "close"
        ? "End this allocation"
        : "Move them to another project";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      subtitle={
        allocation
          ? `${allocation.employeeName} · ${allocation.projectName}`
          : undefined
      }
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : mode === "close" ? "End it" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {mode === "adjust" && (
          <>
            <InlineAlert tone="info">
              Changes the arrangement as though it had always been this. If they are actually
              moving on, end it or reassign instead — those keep what the past looked like.
            </InlineAlert>
            <Field label="Share of their week" required>
              <Input
                type="number"
                min={1}
                max={100}
                value={percentage}
                onChange={(event) => setPercentage(event.target.value)}
              />
            </Field>
            <Field label="Role on the project" required>
              <Select
                value={projectRole}
                onChange={(event) => setProjectRole(event.target.value as ProjectRole)}
              >
                {ROLES.map((role) => (
                  <option key={role} value={role}>
                    {PROJECT_ROLE_LABELS[role]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Status"
              hint="Planned counts towards forecasts; confirmed counts as real capacity."
            >
              <Select
                value={status}
                onChange={(event) => setStatus(event.target.value as AllocationStatus)}
              >
                {STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {ALLOCATION_STATUS_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}

        {mode === "close" && (
          <>
            <InlineAlert tone="info">
              The allocation stays on the record and stops counting from this date. Nothing about
              the weeks it did cover changes.
            </InlineAlert>
            <Field label="Ends on" required>
              <Input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </Field>
          </>
        )}

        {mode === "reassign" && (
          <>
            <InlineAlert tone="info">
              Ends this allocation and opens one on the new project from the same date, so there
              is no day where they are on both or neither.
            </InlineAlert>
            <Field label="Moving to" required>
              <Select
                value={toProjectId}
                onChange={(event) => setToProjectId(event.target.value)}
              >
                <option value="">Choose a project…</option>
                {(projects.data?.content ?? [])
                  .filter((project) => project.id !== allocation?.projectId)
                  .map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label="From" required>
              <Input
                type="date"
                value={effectiveFrom}
                onChange={(event) => setEffectiveFrom(event.target.value)}
              />
            </Field>
            <Field label="Share of their week on the new project" required>
              <Input
                type="number"
                min={1}
                max={100}
                value={percentage}
                onChange={(event) => setPercentage(event.target.value)}
              />
            </Field>
            <Field label="Role there" required>
              <Select
                value={projectRole}
                onChange={(event) => setProjectRole(event.target.value as ProjectRole)}
              >
                {ROLES.map((role) => (
                  <option key={role} value={role}>
                    {PROJECT_ROLE_LABELS[role]}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}
      </div>
    </Drawer>
  );
}
